/**
 * useChatUrlRouting — binds ONE chat embedding's active session to a URL query
 * param, on top of the shared {@link useChatRouting} controller.
 *
 * Every chat surface (the global Luma overlay, the AI Chat page, the AI-agent
 * panel, the entity detail panels) used to hand-roll its own
 * `searchParams.get(param)` read, replace-write, deep-link auto-open and
 * refresh-rebind. They drifted — different params, different sanitization,
 * different close semantics — and that drift is exactly what produced the
 * `?luma=` vs `?chat=` session leak. This hook is the SINGLE code path for all
 * of it. A surface declares only two things — which registry param it binds to,
 * and whether it OWNS that param — and gets identical binding behavior.
 *
 * Two modes, selected by `owned`:
 *
 *   - `owned: true` — page-embedded, self-contained surfaces (the AI Chat page,
 *     the AI-agent panel). The chat lives ON the page and feeds its session id
 *     straight into `useChatSession`, so the hook runs `useChatRouting` and
 *     returns the ROUTED id (with echo-suppression / external-adoption /
 *     reset-on-entity already applied).
 *
 *   - `owned: false` — the global overlay (`GlobalAIButton`), which renders an
 *     `AIChatSidePanel`. That panel is ITSELF the `useChatRouting` controller,
 *     so the overlay must not route a second time — here the hook is pure URL
 *     plumbing: it returns the raw param value + a URL setter to pass straight
 *     into the panel's `chatSessionId` / `onChatSessionChange`.
 *
 * `param` may be `null` to disable URL binding entirely (the overlay yields its
 * param on routes that host their own embedded chat — it stays uncontrolled and
 * never reads/writes the URL there).
 */
import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useChatRouting } from './useChatRouting';

interface UseChatUrlRoutingOptions {
  /**
   * The URL query param this surface binds to (from `CHAT_SESSION_PARAMS`), or
   * `null` to disable URL binding (uncontrolled — used by the global overlay on
   * routes that own their own embedded chat param).
   */
  param: string | null;
  /**
   * Whether this surface OWNS its param. `true` for page-embedded surfaces that
   * feed `useChatSession` directly (controller mode); `false` for the global
   * overlay that delegates routing to its `AIChatSidePanel` (plumbing-only).
   */
  owned: boolean;
  /**
   * Controller mode only: when this key changes the active session resets to
   * `null` (e.g. the panel re-targets a different entity). Ignored when
   * `owned` is `false`.
   */
  resetKey?: string;
}

interface UseChatUrlRoutingReturn {
  /** The active session id bound to the URL param (routed in controller mode). */
  sessionId: string | null;
  /** Synchronous setter — updates state and mirrors the id to the URL param. */
  setSessionId: (sessionId: string | null) => void;
  /**
   * `true` when a session was present in the URL param on first mount — the
   * deep-link signal overlay surfaces use to auto-open. Always `false` for a
   * disabled (`param === null`) binding.
   */
  isDeepLinked: boolean;
}

/**
 * Normalize a raw URL param value. Treats the empty string and the literal
 * strings `"null"` / `"undefined"` (which a bad write or hand-edited link can
 * leave behind) as "no session", so every surface rejects them identically.
 */
function normalizeParamValue(raw: string | null): string | null {
  if (!raw || raw === 'null' || raw === 'undefined') return null;
  return raw;
}

export function useChatUrlRouting({
  param,
  owned,
  resetKey,
}: UseChatUrlRoutingOptions): UseChatUrlRoutingReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawValue = param ? normalizeParamValue(searchParams.get(param)) : null;

  const setParam = useCallback(
    (id: string | null) => {
      if (!param) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set(param, id);
          else next.delete(param);
          return next;
        },
        { replace: true },
      );
    },
    [param, setSearchParams],
  );

  // The controller runs unconditionally (hooks can't be conditional), but in
  // plumbing mode (`owned === false`) we pass `undefined` so it stays
  // uncontrolled and we ignore its result — the overlay's AIChatSidePanel is
  // the real controller, so routing here would double-bind.
  const routed = useChatRouting({
    externalSessionId: owned && param ? rawValue : undefined,
    onExternalSessionChange: owned && param ? setParam : undefined,
    resetKey: owned ? resetKey : undefined,
  });

  // Deep-link signal: a session present in the param on first mount.
  const initialDeepLinkRef = useRef(rawValue);
  const isDeepLinked = initialDeepLinkRef.current !== null;

  if (owned) {
    return {
      sessionId: routed.sessionId,
      setSessionId: routed.setSessionId,
      isDeepLinked,
    };
  }
  return { sessionId: rawValue, setSessionId: setParam, isDeepLinked };
}
